import React, { createContext, useContext, useMemo, useState, useEffect, useRef } from "react";
import { api } from "@/lib/api";

const TRANSLATIONS = {
  de: {
    app_name: "Water Planner",
    tagline: "Wassermanagement für Gemeinden, Wasserversorger und Verbraucher",
    login: "Anmelden",
    logout: "Abmelden",
    register: "Registrieren",
    email: "E-Mail",
    password: "Passwort",
    name: "Name",
    role: "Rolle",
    municipality: "Gemeinde",
    pool_owner: "Poolbesitzer:in",
    dashboard: "Dashboard",
    bookings: "Buchungen",
    calendar: "Kalender",
    messages: "Nachrichten",
    reminders: "Erinnerungen",
    measurements: "Messwerte",
    settings: "Einstellungen",
    daily_quota: "Tageskontingent",
    available_today: "Heute verfügbar",
    used_today: "Heute genutzt",
    address: "Adresse",
    pool_area: "Pool­fläche (m²)",
    pool_depth: "Tiefe (m)",
    volume: "Volumen (m³)",
    date: "Datum",
    status: "Status",
    approve: "Freigeben",
    reject: "Ablehnen",
    complete: "Erledigt",
    delete: "Löschen",
    send: "Senden",
    cancel: "Abbrechen",
    save: "Speichern",
    new_booking: "Neue Buchung",
    book_filling: "Wassermenge buchen",
    pending: "Ausstehend",
    approved: "Freigegeben",
    rejected: "Abgelehnt",
    completed: "Abgeschlossen",
    ph: "pH-Wert",
    chlorine: "Freies Chlor (mg/L)",
    salt: "Salzgehalt (g/L)",
    note: "Notiz",
    add_measurement: "Messwert erfassen",
    water_care: "Wasserpflege",
    backwash: "Filterrückspülung",
    refill: "Wassernachfüllen",
    add_reminder: "Erinnerung hinzufügen",
    interval_days: "Intervall (Tage)",
    due_date: "Fällig",
    my_pool: "Mein Pool",
    large_consumer: "Großverbraucher",
    request_water: "Wasser anfragen",
    refill_report_required: "Meldepflicht Nachfüllen",
    refill_report_required_hint: "Poolbesitzer:innen müssen Nachfüllmengen melden",
    report_refill: "Nachfüllen melden",
    calc_hint: "Volumen = Fläche × Tiefe",
    calc_open: "Volumen berechnen",
    calc_title: "Volumen-Rechner",
    calc_subtitle: "Poolform auswählen und Maße eintragen — die Fläche wird automatisch übernommen.",
    shape_rectangle: "Rechteck",
    shape_round: "Rund",
    shape_oval: "Oval",
    length_m: "Länge (m)",
    width_m: "Breite (m)",
    diameter_m: "Durchmesser (m)",
    calc_area_result: "Berechnete Fläche",
    calc_volume_preview: "Volumen (mit aktueller Tiefe)",
    apply: "Übernehmen",
    calc_formula_rect: "Formel: Länge × Breite",
    calc_formula_round: "Formel: π × (Ø / 2)²",
    calc_formula_oval: "Formel: π × (Länge / 2) × (Breite / 2)",
    quota_exceeded: "Kontingent überschritten",
    contact: "Kontakt",
    no_data: "Keine Daten",
    welcome_back: "Willkommen zurück",
    create_account: "Konto erstellen",
    municipality_name: "Name der Gemeinde",
    select_municipality: "Gemeinde auswählen",
    have_account: "Bereits registriert?",
    no_account: "Noch kein Konto?",
    landing_hero_title: "Wasser fair verteilen. Schwimmbäder, Pools, Grossverbraucher.",
    landing_hero_sub: "Ein Kontingent-basiertes Planungssystem für Gemeinden, Wasserversorger, Poolbesitzer:innen und Grossverbraucher. Transparent. Koordiniert. Digital.",
    get_started: "Jetzt starten",
    features: "Funktionen",
    submit_booking: "Buchung anfragen",
    availability: "Verfügbarkeit prüfen",
    m3: "m³",
    of: "von",
    quota_usage: "Kontingent-Nutzung",
    upcoming: "Bevorstehend",
    history: "Verlauf",
    unit: "Einheit",
    volume_m3: "Volumen (m³)",
    category: "Kategorie",
    agriculture: "Landwirtschaft",
    industry: "Industrie",
    event: "Veranstaltung",
    description: "Beschreibung",
    submit: "Absenden",
    location: "Standort",
    click_map_to_set: "Klick auf Karte, um Position zu setzen",
    no_bookings: "Noch keine Buchungen",
    no_messages: "Noch keine Nachrichten",
    write_message: "Nachricht schreiben...",
    quota_m3: "Kontingent (m³/Tag)",
    view_map: "Auf Karte anzeigen",
    all_bookings: "Alle Buchungen",
    citizens: "Bürger:innen",
    trend: "Verlauf",
    recorded_at: "Erfasst am",
    approve_dialog_title: "Buchung freigeben",
    approval_message: "Nachricht an Poolbesitzer:in",
    approve_send: "Freigeben und senden",
    reset_default: "Standard wiederherstellen",
    placeholders_hint: "Platzhalter: {name}, {date}, {volume}, {municipality}",
    email_settings: "E-Mail-Einstellungen",
    contact_email: "Reply-To E-Mail (Gemeinde)",
    contact_email_hint: "Poolbesitzer:innen können direkt auf E-Mails der Gemeinde antworten.",
    template_de: "Freigabe-Vorlage (Deutsch)",
    template_en: "Freigabe-Vorlage (Englisch)",
    signature_de: "E-Mail-Signatur (Deutsch)",
    signature_en: "E-Mail-Signatur (Englisch)",
    signature_hint: "Wird an Freigabe-, Ablehnungs- und Abschluss-E-Mails angehängt.",
    signature_preview: "Vorschau",
    signature_on_approval: "Bei Freigabe anhängen",
    signature_on_rejection: "Bei Ablehnung anhängen",
    signature_on_completion: "Bei Abschluss anhängen",
    weekly_rollup: "Wochenprognose",
    week: "Woche",
    prefill_toast_title: "Ersatztermin übernommen",
    prefill_toast_desc: "Die Gemeinde hat diesen Termin für Sie vorgeschlagen. Prüfen und absenden.",
    selected: "ausgewählt",
    bulk_approve: "Freigeben",
    bulk_reject: "Ablehnen",
    select_all: "Alle auswählen",
    test_email: "Test-Mail an mich senden",
    test_email_sent: "Test-Mail versendet",
    test_email_error: "E-Mail konnte nicht gesendet werden",
    bulk_approve_count: "Buchungen",
    bulk_placeholder_hint: "Platzhalter werden pro Buchung ersetzt.",
    utility: "Wasserversorger",
    track_muni_title: "Für Gemeinden",
    track_util_title: "Für Wasserversorger",
    track_consumer_title: "Für Verbraucher",
    track_muni_desc: "Kontingente steuern, Buchungen freigeben, Kommunikation.",
    track_util_desc: "Regionale Übersicht, Bedarfsplanung, Grossverbraucher.",
    track_consumer_desc: "Pool buchen, Erinnerungen, Messwerte, direkter Draht zur Gemeinde.",
    utility_dashboard: "Regionale Übersicht",
    total_quota: "Gesamtkontingent",
    total_available: "Regional verfügbar",
    total_used: "Regional genutzt",
    all_municipalities: "Alle Gemeinden",
    demand_curve: "Bedarfskurve",
    forecast: "Prognose",
    projected: "Bewilligt gesamt",
    booked: "Gebucht",
    scope: "Gemeinden",
    save_scope: "Speichern",
    scope_all: "Alle Gemeinden",
    hero_muni_title: "Kontingente steuern. Bürger:innen entlasten.",
    hero_muni_sub: "Legen Sie das Tageskontingent fest, geben Sie Buchungen frei und kommunizieren Sie direkt mit Poolbesitzer:innen.",
    hero_util_title: "Bedarf verstehen. Wasser fair verteilen.",
    hero_util_sub: "Regionale Übersicht über alle Gemeinden, 30-Tage-Bedarfsprognose und Grossverbraucher-Anfragen — in einem Cockpit.",
    hero_consumer_title: "Pool buchen. Ohne Warten.",
    hero_consumer_sub: "Reservieren Sie Ihre Poolbefüllung innerhalb des Kontingents Ihrer Gemeinde. Erinnerungen, Messwerte und direkter Draht — inklusive.",
    forgot_password: "Passwort vergessen?",
    forgot_password_title: "Passwort zurücksetzen",
    forgot_password_hint: "Geben Sie Ihre E-Mail-Adresse ein. Wenn ein Konto existiert, senden wir Ihnen einen Link zum Zurücksetzen — der Link ist 1 Stunde gültig.",
    forgot_password_sent: "Wenn diese E-Mail registriert ist, haben wir einen Link zum Zurücksetzen gesendet. Bitte prüfen Sie Ihren Posteingang.",
    send_reset_link: "Link senden",
    back_to_login: "Zurück zur Anmeldung",
    reset_password_title: "Neues Passwort setzen",
    reset_password_hint: "Wählen Sie ein neues Passwort. Alle bestehenden Sitzungen werden abgemeldet.",
    new_password: "Neues Passwort",
    confirm_password: "Passwort bestätigen",
    passwords_no_match: "Die Passwörter stimmen nicht überein.",
    password_min_chars: "Mindestens 6 Zeichen.",
    reset_password_submit: "Passwort speichern",
    reset_password_success: "Passwort aktualisiert. Bitte melden Sie sich mit dem neuen Passwort an.",
    reset_token_missing: "Der Link ist unvollständig. Bitte fordern Sie einen neuen Link an.",
    password_strength: "Passwortstärke",
    strength_weak: "Schwach",
    strength_fair: "Ausreichend",
    strength_good: "Gut",
    strength_strong: "Stark",
    preview_lang_de: "DE",
    preview_lang_en: "EN",
    download_csv: "Als CSV herunterladen",
    csv_col_week: "Woche",
    csv_col_from: "Von",
    csv_col_to: "Bis",
    csv_col_projected: "Bewilligt gesamt (m³)",
    csv_col_booked: "Gebucht (m³)",
    csv_col_capacity: "Kapazität (m³)",
    csv_col_pct: "Auslastung (%)",
    undo: "Rückgängig",
    bulk_pending_send: "{count} Freigaben werden in {seconds}s versendet",
    bulk_send_now: "Jetzt senden",
    bulk_undone: "Freigabe abgebrochen — keine E-Mails versendet",
    sort_by: "Sortieren nach",
    sort_date_desc: "Datum (neueste zuerst)",
    sort_date_asc: "Datum (älteste zuerst)",
    sort_name: "Name (A–Z)",
    sort_status: "Status",
    filter_status: "Status filtern",
    filter_all: "Alle",
    status_completed_hint: "Prozess abgeschlossen — Poolbefüllung erfolgt und archiviert.",
    mark_as_done: "Als erledigt markieren",
    bulk_dialog_title_reject: "Sammel-Ablehnung",
    bulk_dialog_title_approve: "Sammel-Freigabe",
    recipients_preview: "Empfänger ({count})",
    bulk_placeholder_hint_ext: "Platzhalter {{name}} bzw. {name} werden pro Empfänger:in ersetzt.",
    reject_send: "Ablehnung senden",
    help: "Hilfe",
    help_close: "Schließen",
    waitlist_title: "Ist Ihre Gemeinde interessiert?",
    waitlist_sub: "Kein Vertrag, keine Kosten für den Piloten. Wir melden uns innerhalb von zwei Werktagen mit dem nächsten Schritt.",
    waitlist_muni_field: "Name der Gemeinde",
    waitlist_contact_name: "Ansprechperson",
    waitlist_note: "Was möchten Sie zuerst lösen? (optional)",
    waitlist_submit: "Anfrage senden",
    waitlist_sent: "Vielen Dank — wir melden uns bald bei Ihnen.",
    waitlist_error: "Anfrage konnte nicht gesendet werden. Bitte später erneut versuchen.",
    blog_kicker: "Poolpflege & Tipps",
    blog_title: "Poolpflege & Tipps",
    blog_intro: "Kurze Beiträge zur täglichen Poolpflege, wassersparenden Alltagstricks und der richtigen Chemie.",
    blog_empty: "Aktuell sind keine Beiträge veröffentlicht.",
    blog_not_found: "Beitrag nicht gefunden.",
    blog_all_posts: "Alle Beiträge",
    blog_tile_title: "Poolpflege & Tipps",
    blog_tile_sub: "Kurzartikel rund um Befüllen, Wassersparen und Chemie.",
    blog_admin_title: "Blog-Beiträge",
    blog_admin_hint: "Erstellen, bearbeiten oder löschen Sie Artikel im Bereich „Poolpflege & Tipps\u201C.",
    blog_optional_en: "Englische Fassung (optional)",
    blog_category_hint: "z. B. Befüllen, Sparen, Chemie",
    new_post: "Neuer Beitrag",
    edit_post: "Beitrag bearbeiten",
    post_saved: "Beitrag gespeichert",
    post_deleted: "Beitrag gelöscht",
    published: "Veröffentlicht",
    draft: "Entwurf",
    excerpt: "Kurztext",
    body: "Inhalt",
    category: "Kategorie",
    cover_hue: "Farbton",
    back: "Zurück",
    super_admin_only: "Nur für Super-Admins",
    super_admin_kicker: "Betreiber-Konsole",
    master_dashboard: "Master-Dashboard",
    overview: "Übersicht",
    all_bookings: "Alle Buchungen",
    credits: "Credits & Kosten",
    users: "Nutzer:innen",
    today: "Heute",
    this_month: "Diesen Monat",
    internal_cost: "Interne Kosten",
    margin: "Marge",
    by_feature_month: "Nach Funktion (Monat)",
    by_municipality_month: "Nach Gemeinde (Monat)",
    credit_budget: "Credit-Budget",
    credits_used: "Verbraucht",
    monthly_credit_budget: "Monats-Budget (Credits)",
    set_budget: "Budget setzen",
    budget_saved: "Budget gespeichert",
    new_municipality: "Neue Gemeinde",
    municipality_created: "Gemeinde angelegt",
    settings_saved: "Einstellungen gespeichert",
    weather_api_provider: "Wetter-API Anbieter",
    weather_api_endpoint: "Endpoint",
    weather_api_key: "API-Schlüssel",
    key_set_hint: "gesetzt",
    credit_alert_threshold: "Warnschwelle (Credits/Monat)",
    default_monthly_budget: "Standard-Budget für neue Gemeinden",
    credit_alert_triggered: "Warnschwelle erreicht — bitte prüfen.",
    topup_needed: "Nachkauf empfohlen",
    quota_m3_day: "Kontingent (m³/Tag)",
    settings_short: "Einstellungen",
    municipalities_tab: "Gemeinden",
    // Feature flags & billing (Master dashboard)
    module_live_weather: "Live-Wetter",
    module_credit_billing: "In-App Credit-Verrechnung",
    module_flags: "Module",
    billing_mode: "Abrechnungs-Modus",
    billing_credit: "Credit-Kunde",
    billing_tiered: "Staffelpreis-Kunde",
    tier_fixed_price: "Fixpreis (€/Monat)",
    cost_month_eur: "Kosten (Monat)",
    cost_internal_short: "Emergent-Kosten",
    cost_billed_short: "Gemeinde-Kosten",
    credit_euro_rate: "Umrechnungskurs (€ pro Credit)",
    flags_saved: "Modul-Einstellungen gespeichert",
    // Weather module (Municipality dashboard)
    weather_module_title: "Live-Wetter & Intelligente Warnungen",
    weather_module_benefit: "Automatische Anpassung der Quoten-Empfehlungen basierend auf lokalen Echtzeit-Wetterdaten zur maximalen Ressourcenschonung.",
    weather_module_inactive: "Premium-Modul: Live-Wetter nicht aktiv. Kontaktieren Sie den Administrator für die Freischaltung.",
    weather_module_active: "Live-Wetter aktiv — Empfehlungen basieren auf Echtzeitdaten.",
    weather_alternative: "Wöchentlich aktualisierte, saisonale Experten-Tipps zur Wasserpflege.",
    upgrade_activate_weather: "Jetzt Live-Wetter aktivieren",
    upgrade_dialog_title: "Live-Wetter Modul anfragen",
    upgrade_dialog_body: "Der Administrator erhält eine E-Mail und meldet sich bei Ihnen für die Freischaltung.",
    upgrade_note_label: "Nachricht (optional)",
    upgrade_note_placeholder: "z. B. gewünschter Startzeitpunkt, Rückfragen …",
    upgrade_send: "Anfrage senden",
    upgrade_sent: "Anfrage gesendet — der Administrator meldet sich in Kürze.",
    upgrade_error: "Anfrage konnte nicht gesendet werden.",
    upgrade_rate_limited: "Zu viele Anfragen. Bitte in einer Stunde erneut versuchen.",
    seasonal_tips_title: "Saisonale Poolpflege-Tipps",
    season_winter: "Winter",
    season_spring: "Frühling",
    season_summer: "Sommer",
    season_autumn: "Herbst",
    season_tip_winter: "Frost­schutz prüfen · Rohre entleeren · Skimmer­öffnungen abdecken.",
    season_tip_spring: "Filter reinigen · pH- und Chlorwerte kontrollieren · sanft nachfüllen.",
    season_tip_summer: "Abdeckung gegen Verdunstung · morgens/abends befüllen · Duschen vor dem Baden spart Chlor.",
    season_tip_autumn: "Wasserstand reduzieren · Chemie stabilisieren · rechtzeitig auswintern.",
    // Enterprise
    enterprise_tab: "Enterprise",
    billing_enterprise: "Enterprise-Kunde",
    enterprise_price: "Enterprise-Pauschale (€/Monat)",
    ent_system_load: "Systemlast (Live)",
    ent_realtime: "Aktualisiert alle 15s",
    ent_cores: "Kerne",
    ent_cache_title: "Globales Caching",
    ent_cache_hint: "Puffert Wetter- und KI-Antworten prozessweit, um API-Kosten und Serverlast bei vielen Gleichzeitig-Nutzern zu reduzieren.",
    ent_cache_ttl: "TTL (Sekunden)",
    ent_cache_saved: "Cache-Einstellung gespeichert",
    ent_server_scaling: "Server-Skalierung",
    ent_cpu_cores: "vCPU",
    ent_ram_gb: "RAM (GB)",
    ent_reason_label: "Grund für Skalierung (optional)",
    ent_reason_placeholder: "z. B. Auslastungsspitze, Migration …",
    ent_scale_send: "Skalierungs-Anfrage senden",
    ent_scale_sent: "Skalierungs-Anfrage gesendet",
    ent_capacity_send: "+ Instanzen anfordern",
    ent_capacity_sent: "Kapazitäts-Anfrage gesendet",
    ent_scale_disclaimer: "Konfiguration wird beim Operator angefragt (nicht automatisch provisioniert). E-Mail geht an den Super-Admin.",
    ent_metrics_title: "Performance-Metriken (60 min)",
    ent_metric_requests: "Requests",
    ent_metric_max: "Max",
    ent_metric_errors: "5xx",
    ent_api_keys: "API-Keys",
    ent_key_name: "Bezeichnung",
    ent_rate_limit_min: "Limit /min",
    ent_generate_key: "Key erzeugen",
    ent_keys_empty: "Keine Keys erzeugt.",
    ent_disabled: "widerrufen",
    ent_never_used: "nie verwendet",
    ent_confirm_revoke: "API-Key wirklich widerrufen?",
    ent_key_revoked: "API-Key widerrufen",
    ent_new_key_title: "Neuer API-Key erzeugt",
    ent_new_key_hint: "Dieser Key wird nur einmal angezeigt. Bitte sicher aufbewahren.",
    ent_copy: "Kopieren",
    ent_copied: "Kopiert",
    ent_key_saved_close: "Verstanden",
    ent_webhooks: "Webhooks",
    ent_webhook_url: "Webhook-URL",
    ent_webhook_events: "Events",
    ent_webhook_secret: "Signatur-Secret",
    ent_webhook_secret_hint: "HMAC-SHA256 Signatur (X-WaterPlanner-Signature)",
    ent_webhook_enabled: "Aktiv",
    ent_webhook_saved: "Webhook gespeichert",
    ent_webhook_deliveries: "Letzte Zustellungen",
    ent_webhook_on: "Webhook: an",
    ent_webhook_off: "Webhook: aus",
    ent_endpoint_docs: "Endpunkt-Dokumentation",
    ent_endpoint_docs_hint: "Authentifizierung per Header X-API-Key. Alle Antworten JSON.",
    ent_endpoint_forecast_desc: "Aggregierte Buchungs-Prognose der nächsten X Tage (max 60) für die Gemeinde des API-Keys.",
    ent_endpoint_bookings_desc: "Aktuelle Buchungen der Gemeinde (optional per Status gefiltert).",
    ent_customer_management: "Kunden-Verwaltung (pro Gemeinde)",
    ent_open: "Öffnen",
    ent_collapse: "Schließen",
    utility_pending_title: "Konto wartet auf Freigabe",
    utility_pending_body: "Sie haben Zugriff auf folgende Gemeinden angefragt. Sobald ein Gemeinde-Admin freigibt, erscheinen die Daten hier automatisch.",
    utility_pending_no_data: "Aktuell wurde noch keine Ihrer Anfragen freigegeben. Bitte melden Sie sich später erneut an.",
    utility_requested_scope: "Angefragte Gemeinden",
    utility_register_scope_label: "Welche Gemeinden möchten Sie betreuen?",
    utility_register_scope_hint: "Wählen Sie eine oder mehrere. Ein Admin muss Ihre Anfrage bestätigen, bevor Sie Daten sehen.",
    pending_utilities: "Versorger-Anfragen",
    pending_utilities_empty: "Aktuell warten keine Versorger auf Freigabe.",
    approve_utility: "Freigeben",
    reject_utility: "Ablehnen",
    approve_utility_toast: "Versorger freigegeben",
    reject_utility_toast: "Anfrage abgelehnt",
    booking_request_sent: "Buchungsanfrage gesendet",
    sign_in_google: "Mit Google anmelden",
    or_divider: "oder",
    pick_date: "Datum wählen",
    reminder_added: "Erinnerung hinzugefügt",
    forecast_booked_pool: "Bewilligt · Poolbesitzer",
    forecast_pending: "Offene Anfragen (nicht bewilligt)",
    forecast_load: "Auslastung",
    forecast_load_warn: "ab 80 % Auslastung (bewilligt)",
    forecast_load_over: "ab 100 % – Kontingent überschritten",
    forecast_booked_large: "Bewilligt · Großverbraucher",
    forecast_all_bookings_hint: "Zeigt ausschließlich die aktuell bewilligten künftigen Buchungen von Poolbesitzern und Großverbrauchern – tagesgenau wie genehmigt, ohne Hochrechnung aus vergangenen Buchungen. Offene Anfragen zeigt die helle gestrichelte Linie.",
    forecast_cumulative: "Alle Gemeinden (kumuliert)",
    forecast_by_municipality: "Prognose je Gemeinde (30 Tage)",
    forecast_peak: "Spitzenauslastung",
    overbooking_title: "Tag wäre überbucht",
    overbooked_legend: "Überbucht",
    street_house: "Straße und Hausnummer",
    postal_code: "PLZ",
    city: "Ort",
    address_assign_hint: "Ihre Gemeinde wird automatisch anhand von PLZ und Ort ermittelt. Pro Adresse ist nur ein Konto möglich.",
    map_location_optional: "Standort auf der Karte (optional)",
    account_pending_title: "Ihr Zugang wird geprüft",
    account_pending_approval_text: "Ihre Gemeinde gibt neue Konten manuell frei. Sie erhalten eine E-Mail, sobald Sie buchen können.",
    account_pending_assignment_text: "Ihre Adresse konnte keiner Gemeinde eindeutig zugeordnet werden. Wir haben die Verwaltung informiert – Sie erhalten eine E-Mail, sobald Ihr Zugang freigeschaltet ist.",
    account_rejected_title: "Zugang nicht freigegeben",
    account_rejected_text: "Ihre Registrierung wurde von der Gemeinde nicht freigegeben. Bitte wenden Sie sich direkt an Ihre Gemeinde.",
    check_again: "Status prüfen",
    registration_settings: "Registrierung neuer Nutzer",
    manual_user_approval: "Manuelle Freischaltung neuer Nutzer",
    manual_user_approval_hint: "Ist der Schalter aktiv, müssen neue Poolbesitzer und Großverbraucher von Ihnen freigegeben werden. Sonst sind sie sofort aktiv.",
    postal_areas: "PLZ und Orte Ihrer Gemeinde",
    postal_areas_hint: "Neue Registrierungen mit dieser PLZ/Ort-Kombination werden automatisch Ihrer Gemeinde zugeordnet.",
    postal_areas_empty: "Noch keine PLZ hinterlegt.",
    postal_import_title: "Liste einfügen",
    postal_import_hint: "Eine Zeile pro Eintrag, z. B. „9991 Zwischenwasser“, „9991;Zwischenwasser“ oder direkt aus Excel kopiert. Doppelte Einträge werden übersprungen.",
    postal_import_recognized: "Erkannt: {n}",
    postal_import_invalid: "Nicht erkannt: {n}",
    postal_import_apply: "In Liste übernehmen",
    postal_import_done: "Übernommen: {added} · bereits vorhanden: {dupes} – bitte speichern",
    postal_areas_clear: "Alle entfernen",
    postal_areas_unsaved_hint: "Änderungen werden erst mit „Speichern“ wirksam.",
    map_auto_searching: "Standort wird anhand Ihrer Adresse gesucht …",
    map_auto_found: "Standort aus Ihrer Adresse gesetzt – bei Bedarf auf der Karte korrigieren.",
    map_auto_not_found: "Adresse nicht auf der Karte gefunden – klicken Sie optional auf Ihren Standort.",
    postal_area_conflict: "{area} ist auch bei „{muni}“ hinterlegt – neue Registrierungen dort müssen manuell zugeordnet werden.",
    pending_users_title: "Neue Nutzer freigeben",
    status_pending_approval: "Wartet auf Freigabe",
    status_pending_assignment: "Gemeinde unklar",
    user_approved_toast: "Nutzer freigeschaltet",
    user_rejected_toast: "Registrierung abgelehnt",
    pending_assignments_title: "Zuordnung ausstehend",
    pending_assignments_desc: "Registrierungen, deren Adresse keiner Gemeinde eindeutig zugeordnet werden konnte.",
    pending_assignments_empty: "Keine offenen Zuordnungen.",
    candidates: "Mögliche Gemeinden",
    no_candidate: "Keine Gemeinde mit dieser PLZ hinterlegt",
    assign: "Zuweisen",
    user_assigned_toast: "Nutzer zugewiesen und freigeschaltet",
    overbooked_days: "Überbuchte Tage in diesem Monat",
    lc_free_on: "Am {date} noch frei",
    lc_over_hint: "Ihre Menge liegt {over} m³ über dem freien Kontingent. Sie können den Antrag trotzdem stellen – die Gemeinde entscheidet.",
    overbooking_intro: "Mit diesem Antrag wäre das Tageskontingent am {date} überschritten.",
    overbooking_request: "Dieser Antrag",
    overbooking_total: "Gesamt an diesem Tag",
    overbooking_over: "Überbuchung",
    overbooking_question: "Möchten Sie den Antrag trotzdem genehmigen?",
    overbooking_confirm: "Trotzdem genehmigen",
    overbooking_approved_toast: "Genehmigt – der Tag ist jetzt überbucht",
    failed_logins: "Anmelde-Protokoll",
    failed_logins_desc: "Die letzten fehlgeschlagenen Anmeldungen (Aufbewahrung 30 Tage). Viele Versuche von einer IP auf mehrere Konten deuten auf einen Angriff hin.",
    failed_logins_24h: "Fehlversuche in den letzten 24 Stunden",
    failed_logins_accounts: "Konten",
    failed_logins_empty: "Keine fehlgeschlagenen Anmeldungen.",
    failed_logins_time: "Zeitpunkt",
    failed_logins_reason: "Grund",
    reason_wrong_password: "Falsches Passwort",
    reason_unknown_email: "Unbekannte E-Mail",
    reason_lockout_triggered: "Sperre ausgelöst",
    reason_locked: "Versuch während Sperre",
    locked_accounts: "Gesperrte Konten",
    locked_accounts_desc: "Konten werden nach 5 falschen Passwörtern innerhalb von 15 Minuten für 15 Minuten gesperrt. Entsperrung automatisch, per Passwort-Reset oder hier manuell.",
    locked_accounts_empty: "Aktuell ist kein Konto gesperrt.",
    locked_until: "gesperrt bis",
    unlock_account: "Entsperren",
    account_unlocked: "Konto entsperrt",
  },
  en: {
    app_name: "Water Planner",
    tagline: "Water management for municipalities, utilities and consumers",
    login: "Sign in",
    logout: "Sign out",
    register: "Register",
    email: "Email",
    password: "Password",
    name: "Name",
    role: "Role",
    municipality: "Municipality",
    pool_owner: "Pool owner",
    dashboard: "Dashboard",
    bookings: "Bookings",
    calendar: "Calendar",
    messages: "Messages",
    reminders: "Reminders",
    measurements: "Measurements",
    settings: "Settings",
    daily_quota: "Daily quota",
    available_today: "Available today",
    used_today: "Used today",
    address: "Address",
    pool_area: "Pool area (m²)",
    pool_depth: "Depth (m)",
    volume: "Volume (m³)",
    date: "Date",
    status: "Status",
    approve: "Approve",
    reject: "Reject",
    complete: "Complete",
    delete: "Delete",
    send: "Send",
    cancel: "Cancel",
    save: "Save",
    new_booking: "New booking",
    book_filling: "Book water volume",
    pending: "Pending",
    approved: "Approved",
    rejected: "Rejected",
    completed: "Completed",
    ph: "pH",
    chlorine: "Free chlorine (mg/L)",
    salt: "Salt (g/L)",
    note: "Note",
    add_measurement: "Log measurement",
    water_care: "Water care",
    backwash: "Filter backwash",
    refill: "Water refill",
    add_reminder: "Add reminder",
    interval_days: "Interval (days)",
    due_date: "Due",
    my_pool: "My pool",
    large_consumer: "Large consumer",
    request_water: "Request water",
    refill_report_required: "Refill reporting required",
    refill_report_required_hint: "Owners must report refill amounts",
    report_refill: "Report refill",
    calc_hint: "Volume = Area × Depth",
    calc_open: "Calculate volume",
    calc_title: "Volume calculator",
    calc_subtitle: "Pick the pool shape and enter the dimensions — area is filled in automatically.",
    shape_rectangle: "Rectangle",
    shape_round: "Round",
    shape_oval: "Oval",
    length_m: "Length (m)",
    width_m: "Width (m)",
    diameter_m: "Diameter (m)",
    calc_area_result: "Computed area",
    calc_volume_preview: "Volume (with current depth)",
    apply: "Apply",
    calc_formula_rect: "Formula: Length × Width",
    calc_formula_round: "Formula: π × (Ø / 2)²",
    calc_formula_oval: "Formula: π × (Length / 2) × (Width / 2)",
    quota_exceeded: "Quota exceeded",
    contact: "Contact",
    no_data: "No data",
    welcome_back: "Welcome back",
    create_account: "Create account",
    municipality_name: "Municipality name",
    select_municipality: "Select municipality",
    have_account: "Already have an account?",
    no_account: "No account yet?",
    landing_hero_title: "Share water fairly. Pools, spas, large consumers.",
    landing_hero_sub: "A quota-based planning system for municipalities, utilities, pool owners and large consumers. Transparent. Coordinated. Digital.",
    get_started: "Get started",
    features: "Features",
    submit_booking: "Request booking",
    availability: "Check availability",
    m3: "m³",
    of: "of",
    quota_usage: "Quota usage",
    upcoming: "Upcoming",
    history: "History",
    unit: "Unit",
    volume_m3: "Volume (m³)",
    category: "Category",
    agriculture: "Agriculture",
    industry: "Industry",
    event: "Event",
    description: "Description",
    submit: "Submit",
    location: "Location",
    click_map_to_set: "Click on map to set position",
    no_bookings: "No bookings yet",
    no_messages: "No messages yet",
    write_message: "Write a message...",
    quota_m3: "Quota (m³/day)",
    view_map: "Show on map",
    all_bookings: "All bookings",
    citizens: "Citizens",
    trend: "Trend",
    recorded_at: "Recorded at",
    approve_dialog_title: "Approve booking",
    approval_message: "Message to pool owner",
    approve_send: "Approve & send",
    reset_default: "Reset to default",
    placeholders_hint: "Placeholders: {name}, {date}, {volume}, {municipality}",
    email_settings: "Email settings",
    contact_email: "Reply-to email (municipality)",
    contact_email_hint: "Pool owners can reply directly to the town hall.",
    template_de: "Approval template (German)",
    template_en: "Approval template (English)",
    signature_de: "Email signature (German)",
    signature_en: "Email signature (English)",
    signature_hint: "Appended to approval, rejection and completion emails.",
    signature_preview: "Preview",
    signature_on_approval: "Append on approval",
    signature_on_rejection: "Append on rejection",
    signature_on_completion: "Append on completion",
    weekly_rollup: "Weekly outlook",
    week: "Week",
    prefill_toast_title: "Replacement slot loaded",
    prefill_toast_desc: "Your municipality suggested this date. Review and submit.",
    selected: "selected",
    bulk_approve: "Approve",
    bulk_reject: "Reject",
    select_all: "Select all",
    test_email: "Send test email to me",
    test_email_sent: "Test email sent",
    test_email_error: "Email could not be sent",
    bulk_approve_count: "bookings",
    bulk_placeholder_hint: "Placeholders are substituted per booking.",
    utility: "Utility",
    track_muni_title: "For municipalities",
    track_util_title: "For utilities",
    track_consumer_title: "For consumers",
    track_muni_desc: "Steer quotas, approve bookings, communicate with citizens.",
    track_util_desc: "Regional overview, demand planning, large consumers.",
    track_consumer_desc: "Book your pool, reminders, measurements, direct line to the town hall.",
    utility_dashboard: "Regional overview",
    total_quota: "Total quota",
    total_available: "Regional available",
    total_used: "Regional used",
    all_municipalities: "All municipalities",
    demand_curve: "Demand curve",
    forecast: "Forecast",
    projected: "Approved total",
    booked: "Booked",
    scope: "Municipalities",
    save_scope: "Save",
    scope_all: "All municipalities",
    hero_muni_title: "Steer quotas. Support your citizens.",
    hero_muni_sub: "Set daily quotas, approve or reject bookings and message pool owners directly.",
    hero_util_title: "Understand demand. Share water fairly.",
    hero_util_sub: "Regional view across every municipality, 30-day demand forecast and large consumer requests — one cockpit.",
    hero_consumer_title: "Book your pool. Without the wait.",
    hero_consumer_sub: "Reserve your pool filling inside your municipality's quota. Reminders, measurements and a direct line to the town hall included.",
    forgot_password: "Forgot password?",
    forgot_password_title: "Reset your password",
    forgot_password_hint: "Enter your email. If an account exists, we'll send a reset link — valid for 1 hour.",
    forgot_password_sent: "If that email is registered, we've sent a reset link. Please check your inbox.",
    send_reset_link: "Send reset link",
    back_to_login: "Back to sign in",
    reset_password_title: "Choose a new password",
    reset_password_hint: "Pick a new password. Any existing sessions will be signed out.",
    new_password: "New password",
    confirm_password: "Confirm password",
    passwords_no_match: "Passwords do not match.",
    password_min_chars: "At least 6 characters.",
    reset_password_submit: "Save password",
    reset_password_success: "Password updated. Please sign in with your new password.",
    reset_token_missing: "The link is incomplete. Please request a new one.",
    password_strength: "Password strength",
    strength_weak: "Weak",
    strength_fair: "Fair",
    strength_good: "Good",
    strength_strong: "Strong",
    preview_lang_de: "DE",
    preview_lang_en: "EN",
    download_csv: "Download CSV",
    csv_col_week: "Week",
    csv_col_from: "From",
    csv_col_to: "To",
    csv_col_projected: "Approved total (m³)",
    csv_col_booked: "Booked (m³)",
    csv_col_capacity: "Capacity (m³)",
    csv_col_pct: "Utilisation (%)",
    undo: "Undo",
    bulk_pending_send: "{count} approvals will be sent in {seconds}s",
    bulk_send_now: "Send now",
    bulk_undone: "Approvals cancelled — no emails sent",
    sort_by: "Sort by",
    sort_date_desc: "Date (newest first)",
    sort_date_asc: "Date (oldest first)",
    sort_name: "Name (A–Z)",
    sort_status: "Status",
    filter_status: "Filter status",
    filter_all: "All",
    status_completed_hint: "Process complete — pool filling done and archived.",
    mark_as_done: "Mark as done",
    bulk_dialog_title_reject: "Bulk rejection",
    bulk_dialog_title_approve: "Bulk approval",
    recipients_preview: "Recipients ({count})",
    bulk_placeholder_hint_ext: "Placeholders {{name}} and {name} are replaced per recipient.",
    reject_send: "Send rejection",
    help: "Help",
    help_close: "Close",
    waitlist_title: "Interested for your municipality?",
    waitlist_sub: "No contract, no cost for the pilot. We'll reply within two working days with the next step.",
    waitlist_muni_field: "Municipality name",
    waitlist_contact_name: "Contact person",
    waitlist_note: "What would you like to solve first? (optional)",
    waitlist_submit: "Send request",
    waitlist_sent: "Thank you — we'll be in touch shortly.",
    waitlist_error: "Request could not be sent. Please try again later.",
    blog_kicker: "Pool care & tips",
    blog_title: "Pool care & tips",
    blog_intro: "Short posts on everyday pool care, water-saving tricks and balanced chemistry.",
    blog_empty: "No posts published yet.",
    blog_not_found: "Post not found.",
    blog_all_posts: "All posts",
    blog_tile_title: "Pool care & tips",
    blog_tile_sub: "Bite-size articles on filling, saving water and chemistry.",
    blog_admin_title: "Blog posts",
    blog_admin_hint: "Create, edit or remove articles in \"Pool care & tips\".",
    blog_optional_en: "English version (optional)",
    blog_category_hint: "e.g. Filling, Saving, Chemistry",
    new_post: "New post",
    edit_post: "Edit post",
    post_saved: "Post saved",
    post_deleted: "Post deleted",
    published: "Published",
    draft: "Draft",
    excerpt: "Excerpt",
    body: "Body",
    category: "Category",
    cover_hue: "Hue",
    back: "Back",
    super_admin_only: "Super admins only",
    super_admin_kicker: "Operator console",
    master_dashboard: "Master dashboard",
    overview: "Overview",
    all_bookings: "All bookings",
    credits: "Credits & costs",
    users: "Users",
    today: "Today",
    this_month: "This month",
    internal_cost: "Internal cost",
    margin: "Margin",
    by_feature_month: "By feature (month)",
    by_municipality_month: "By municipality (month)",
    credit_budget: "Credit budget",
    credits_used: "Used",
    monthly_credit_budget: "Monthly budget (credits)",
    set_budget: "Set budget",
    budget_saved: "Budget saved",
    new_municipality: "New municipality",
    municipality_created: "Municipality created",
    settings_saved: "Settings saved",
    weather_api_provider: "Weather API provider",
    weather_api_endpoint: "Endpoint",
    weather_api_key: "API key",
    key_set_hint: "set",
    credit_alert_threshold: "Alert threshold (credits/month)",
    default_monthly_budget: "Default budget for new municipalities",
    credit_alert_triggered: "Alert threshold reached — please review.",
    topup_needed: "Top-up recommended",
    quota_m3_day: "Quota (m³/day)",
    settings_short: "Settings",
    municipalities_tab: "Municipalities",
    // Feature flags & billing (Master dashboard)
    module_live_weather: "Live weather",
    module_credit_billing: "In-app credit billing",
    module_flags: "Modules",
    billing_mode: "Billing mode",
    billing_credit: "Credit customer",
    billing_tiered: "Tiered customer",
    tier_fixed_price: "Fixed price (€/month)",
    cost_month_eur: "Cost (month)",
    cost_internal_short: "Emergent cost",
    cost_billed_short: "Municipality cost",
    credit_euro_rate: "Conversion rate (€ per credit)",
    flags_saved: "Module settings saved",
    // Weather module (Municipality dashboard)
    weather_module_title: "Live weather & smart alerts",
    weather_module_benefit: "Automatic quota-recommendation adjustments based on local real-time weather data — maximal resource protection.",
    weather_module_inactive: "Premium module: Live weather is not active. Contact your administrator to unlock.",
    weather_module_active: "Live weather active — recommendations use real-time data.",
    weather_alternative: "Weekly-refreshed seasonal expert tips for water care.",
    upgrade_activate_weather: "Activate live weather now",
    upgrade_dialog_title: "Request live-weather module",
    upgrade_dialog_body: "The administrator receives an email and will get back to you to activate the module.",
    upgrade_note_label: "Message (optional)",
    upgrade_note_placeholder: "e.g. preferred start date, questions …",
    upgrade_send: "Send request",
    upgrade_sent: "Request sent — the administrator will get back to you shortly.",
    upgrade_error: "Could not send request.",
    upgrade_rate_limited: "Too many requests. Please try again in an hour.",
    seasonal_tips_title: "Seasonal pool-care tips",
    season_winter: "Winter",
    season_spring: "Spring",
    season_summer: "Summer",
    season_autumn: "Autumn",
    season_tip_winter: "Check frost protection · drain pipes · cover skimmer openings.",
    season_tip_spring: "Clean the filter · check pH & chlorine · refill gently.",
    season_tip_summer: "Cover against evaporation · refill morning/evening · a shower before swimming saves chlorine.",
    season_tip_autumn: "Lower water level · stabilise chemistry · winterise on time.",
    // Enterprise
    enterprise_tab: "Enterprise",
    billing_enterprise: "Enterprise customer",
    enterprise_price: "Enterprise flat rate (€/month)",
    ent_system_load: "System load (live)",
    ent_realtime: "Refreshed every 15s",
    ent_cores: "cores",
    ent_cache_title: "Global cache",
    ent_cache_hint: "Buffers weather & AI answers process-wide to cut API costs and server load under high concurrency.",
    ent_cache_ttl: "TTL (seconds)",
    ent_cache_saved: "Cache setting saved",
    ent_server_scaling: "Server scaling",
    ent_cpu_cores: "vCPU",
    ent_ram_gb: "RAM (GB)",
    ent_reason_label: "Reason (optional)",
    ent_reason_placeholder: "e.g. load spike, migration …",
    ent_scale_send: "Send scaling request",
    ent_scale_sent: "Scaling request sent",
    ent_capacity_send: "+ Instances request",
    ent_capacity_sent: "Capacity request sent",
    ent_scale_disclaimer: "Configuration is requested from the operator (not auto-provisioned). Email goes to super admin.",
    ent_metrics_title: "Performance metrics (60 min)",
    ent_metric_requests: "Requests",
    ent_metric_max: "Max",
    ent_metric_errors: "5xx",
    ent_api_keys: "API keys",
    ent_key_name: "Label",
    ent_rate_limit_min: "Limit /min",
    ent_generate_key: "Generate key",
    ent_keys_empty: "No keys created.",
    ent_disabled: "revoked",
    ent_never_used: "never used",
    ent_confirm_revoke: "Really revoke this API key?",
    ent_key_revoked: "API key revoked",
    ent_new_key_title: "New API key generated",
    ent_new_key_hint: "This key is shown only once. Store it safely.",
    ent_copy: "Copy",
    ent_copied: "Copied",
    ent_key_saved_close: "Got it",
    ent_webhooks: "Webhooks",
    ent_webhook_url: "Webhook URL",
    ent_webhook_events: "Events",
    ent_webhook_secret: "Signature secret",
    ent_webhook_secret_hint: "HMAC-SHA256 signature (X-WaterPlanner-Signature)",
    ent_webhook_enabled: "Enabled",
    ent_webhook_saved: "Webhook saved",
    ent_webhook_deliveries: "Recent deliveries",
    ent_webhook_on: "Webhook: on",
    ent_webhook_off: "Webhook: off",
    ent_endpoint_docs: "Endpoint documentation",
    ent_endpoint_docs_hint: "Authentication via header X-API-Key. All responses JSON.",
    ent_endpoint_forecast_desc: "Aggregated booking forecast for the next X days (max 60) for the API-key's municipality.",
    ent_endpoint_bookings_desc: "Current bookings of the municipality (optionally filtered by status).",
    ent_customer_management: "Customer management (per municipality)",
    ent_open: "Open",
    ent_collapse: "Close",
    utility_pending_title: "Account pending approval",
    utility_pending_body: "You requested access to the following municipalities. Once a municipality admin approves, data will appear here automatically.",
    utility_pending_no_data: "No requests have been approved yet. Please sign in again later.",
    utility_requested_scope: "Requested municipalities",
    utility_register_scope_label: "Which municipalities do you supervise?",
    utility_register_scope_hint: "Pick one or more. An admin must approve your request before you can see data.",
    pending_utilities: "Utility requests",
    pending_utilities_empty: "No utilities are waiting for approval.",
    approve_utility: "Approve",
    reject_utility: "Reject",
    approve_utility_toast: "Utility approved",
    reject_utility_toast: "Request rejected",
    booking_request_sent: "Booking request sent",
    sign_in_google: "Sign in with Google",
    or_divider: "or",
    pick_date: "Pick a date",
    reminder_added: "Reminder added",
    forecast_booked_pool: "Approved · pool owners",
    forecast_pending: "Open requests (not approved)",
    forecast_load: "Utilisation",
    forecast_load_warn: "from 80 % utilisation (approved)",
    forecast_load_over: "from 100 % – quota exceeded",
    forecast_booked_large: "Approved · large consumers",
    forecast_all_bookings_hint: "Shows only the currently approved future bookings of pool owners and large consumers – per day exactly as approved, without extrapolating from past bookings. The light dashed line shows open requests.",
    forecast_cumulative: "All municipalities (cumulative)",
    forecast_by_municipality: "Forecast by municipality (30 days)",
    forecast_peak: "Peak utilisation",
    overbooking_title: "Day would be overbooked",
    overbooked_legend: "Overbooked",
    street_house: "Street and number",
    postal_code: "Postcode",
    city: "Town",
    address_assign_hint: "Your municipality is determined automatically from postcode and town. Only one account per address.",
    map_location_optional: "Location on map (optional)",
    account_pending_title: "Your access is being reviewed",
    account_pending_approval_text: "Your municipality approves new accounts manually. You'll get an email as soon as you can book.",
    account_pending_assignment_text: "Your address could not be matched to a single municipality. We've informed the administration – you'll get an email once your access is enabled.",
    account_rejected_title: "Access not approved",
    account_rejected_text: "Your registration was not approved by the municipality. Please contact your municipality directly.",
    check_again: "Check status",
    registration_settings: "New user registration",
    manual_user_approval: "Manual approval of new users",
    manual_user_approval_hint: "When enabled, new pool owners and large consumers must be approved by you. Otherwise they're active immediately.",
    postal_areas: "Postcodes and towns of your municipality",
    postal_areas_hint: "New registrations with this postcode/town are assigned to your municipality automatically.",
    postal_areas_empty: "No postcodes yet.",
    postal_import_title: "Paste a list",
    postal_import_hint: "One entry per line, e.g. “9991 Zwischenwasser”, “9991;Zwischenwasser” or copied from Excel. Duplicates are skipped.",
    postal_import_recognized: "Recognised: {n}",
    postal_import_invalid: "Not recognised: {n}",
    postal_import_apply: "Add to list",
    postal_import_done: "{added} added, {dupes} already present – please save",
    postal_areas_clear: "Remove all",
    postal_areas_unsaved_hint: "Changes take effect after “Save”.",
    map_auto_searching: "Locating your address …",
    map_auto_found: "Location set from your address – adjust on the map if needed.",
    map_auto_not_found: "Address not found on the map – optionally click your location.",
    postal_area_conflict: "{area} is also used by “{muni}” – new registrations there need manual assignment.",
    pending_users_title: "Approve new users",
    status_pending_approval: "Awaiting approval",
    status_pending_assignment: "Municipality unclear",
    user_approved_toast: "User approved",
    user_rejected_toast: "Registration rejected",
    pending_assignments_title: "Assignment pending",
    pending_assignments_desc: "Registrations whose address could not be matched to a single municipality.",
    pending_assignments_empty: "No open assignments.",
    candidates: "Possible municipalities",
    no_candidate: "No municipality has this postcode",
    assign: "Assign",
    user_assigned_toast: "User assigned and activated",
    overbooked_days: "Overbooked days this month",
    lc_free_on: "Still available on {date}",
    lc_over_hint: "Your volume exceeds the free quota by {over} m³. You can still submit – the municipality decides.",
    overbooking_intro: "Approving this request would exceed the daily quota on {date}.",
    overbooking_request: "This request",
    overbooking_total: "Total on this day",
    overbooking_over: "Overbooked by",
    overbooking_question: "Approve the request anyway?",
    overbooking_confirm: "Approve anyway",
    overbooking_approved_toast: "Approved – the day is now overbooked",
    failed_logins: "Sign-in log",
    failed_logins_desc: "Most recent failed sign-ins (kept for 30 days). Many attempts from one IP across several accounts suggest an attack.",
    failed_logins_24h: "Failed attempts in the last 24 hours",
    failed_logins_accounts: "accounts",
    failed_logins_empty: "No failed sign-ins.",
    failed_logins_time: "Time",
    failed_logins_reason: "Reason",
    reason_wrong_password: "Wrong password",
    reason_unknown_email: "Unknown email",
    reason_lockout_triggered: "Lockout triggered",
    reason_locked: "Attempt while locked",
    locked_accounts: "Locked accounts",
    locked_accounts_desc: "Accounts are locked for 15 minutes after 5 wrong passwords within 15 minutes. Unlock happens automatically, via password reset or manually here.",
    locked_accounts_empty: "No account is currently locked.",
    locked_until: "locked until",
    unlock_account: "Unlock",
    account_unlocked: "Account unlocked",
  },
};

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, _setLang] = useState(() => localStorage.getItem("pwp_lang") || "de");
  const primed = useRef(false);

  useEffect(() => {
    localStorage.setItem("pwp_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = (next, { syncBackend = true } = {}) => {
    _setLang(next);
    if (syncBackend) {
      api.patch("/auth/preferences", { language: next }).catch(() => {});
    }
  };

  const primeFromUser = (userLang) => {
    if (primed.current) return;
    primed.current = true;
    if (userLang && userLang !== lang) _setLang(userLang);
  };

  const t = useMemo(
    () => (key) => TRANSLATIONS[lang][key] ?? TRANSLATIONS.de[key] ?? key,
    [lang],
  );

  return (
    <I18nContext.Provider value={{ lang, setLang, t, primeFromUser }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("I18nProvider missing");
  return ctx;
}
